import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  KeyRound, Plus, X, Search, RefreshCw, AlertTriangle, CheckCircle2, XCircle,
  Loader2, Cloud, ShieldCheck, Server, Database, ExternalLink,
} from 'lucide-react';

const API = `${import.meta.env.VITE_API_BASE || ''}/api`;

// Broker type → { label, icon, color, config field schema }
const BROKER_TYPES = {
  hashicorp_vault: {
    label: 'HashiCorp Vault',
    Icon: ShieldCheck,
    color: 'text-amber-300',
    fields: [
      { key: 'url', label: 'Vault URL', placeholder: 'https://vault.company.com:8200', required: true },
      { key: 'token', label: 'Vault Token (read-only)', placeholder: 's.aBcDeF…', required: true, secret: true },
      { key: 'namespace', label: 'Namespace (Enterprise)', placeholder: 'optional' },
      { key: 'mount_point', label: 'KV v2 mount point', placeholder: 'secret (default)' },
      { key: 'path_prefix', label: 'Path prefix', placeholder: 'optional — restrict listing' },
    ],
  },
  aws_secrets_manager: {
    label: 'AWS Secrets Manager',
    Icon: Cloud,
    color: 'text-orange-300',
    hint: '$0.40/secret/month · native rotation · best for real secrets',
    fields: [
      { key: 'region', label: 'AWS Region', placeholder: 'us-east-1', required: true },
      { key: 'role_arn', label: 'IAM Role ARN (optional)', placeholder: 'arn:aws:iam::123456789012:role/CloudGenieReader' },
      { key: 'aws_access_key_id', label: 'Access Key ID (optional)', placeholder: 'AKIA…' },
      { key: 'aws_secret_access_key', label: 'Secret Access Key', placeholder: 'optional', secret: true },
      { key: 'name_prefix', label: 'Name prefix filter', placeholder: 'optional' },
    ],
  },
  aws_ssm_parameter_store: {
    label: 'AWS SSM Parameter Store',
    Icon: Cloud,
    color: 'text-amber-300',
    hint: 'Free for 10k standard params · use for config + small secrets',
    fields: [
      { key: 'region', label: 'AWS Region', placeholder: 'us-east-1', required: true },
      { key: 'role_arn', label: 'IAM Role ARN (optional)', placeholder: 'arn:aws:iam::123456789012:role/CloudGenieReader' },
      { key: 'aws_access_key_id', label: 'Access Key ID (optional)', placeholder: 'AKIA…' },
      { key: 'aws_secret_access_key', label: 'Secret Access Key', placeholder: 'optional', secret: true },
      { key: 'path_prefix', label: 'Path prefix filter', placeholder: '/prod/api/ (recommended)' },
    ],
  },
  azure_key_vault: {
    label: 'Azure Key Vault',
    Icon: Server,
    color: 'text-cyan-300',
    hint: 'Encrypted key/cert store · use for real secrets',
    fields: [
      { key: 'vault_url', label: 'Vault URL', placeholder: 'https://my-kv.vault.azure.net', required: true },
      { key: 'tenant_id', label: 'Tenant ID', placeholder: 'optional (falls back to ambient auth)' },
      { key: 'client_id', label: 'Client ID', placeholder: 'optional' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'optional', secret: true },
    ],
  },
  azure_app_configuration: {
    label: 'Azure App Configuration',
    Icon: Server,
    color: 'text-sky-300',
    hint: 'Free tier: 1 store / 10 MB · use for app config + feature flags',
    fields: [
      { key: 'endpoint', label: 'Endpoint', placeholder: 'https://<store>.azconfig.io (or use connection string below)' },
      { key: 'connection_string', label: 'Connection String', placeholder: 'Endpoint=…;Id=…;Secret=… (skips fields above)', secret: true },
      { key: 'tenant_id', label: 'Tenant ID', placeholder: 'optional' },
      { key: 'client_id', label: 'Client ID', placeholder: 'optional' },
      { key: 'client_secret', label: 'Client Secret', placeholder: 'optional', secret: true },
      { key: 'key_prefix', label: 'Key prefix filter', placeholder: 'optional — e.g. app:api:' },
      { key: 'label_filter', label: 'Label filter', placeholder: 'optional — e.g. prod' },
    ],
  },
  gcp_secret_manager: {
    label: 'GCP Secret Manager',
    Icon: Database,
    color: 'text-emerald-300',
    fields: [
      { key: 'project_id', label: 'GCP Project ID', placeholder: 'my-project-123', required: true },
      { key: 'service_account_json', label: 'Service Account JSON (paste full JSON)', placeholder: '{ "type": "service_account", ... }', secret: true, textarea: true },
    ],
  },
};

const HEALTH_STYLE = {
  ok:        { label: 'Healthy',   color: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/40', Icon: CheckCircle2 },
  unhealthy: { label: 'Unhealthy', color: 'text-rose-300',    bg: 'bg-rose-500/10 border-rose-500/40',       Icon: XCircle },
  unknown:   { label: 'Unknown',   color: 'text-slate-400',   bg: 'bg-slate-800 border-slate-700',           Icon: AlertTriangle },
};

export default function SecretBrokers() {
  const [brokers, setBrokers] = useState([]);
  const [federated, setFederated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [activeBroker, setActiveBroker] = useState(null);   // for details drawer

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/secret-brokers`, { withCredentials: true });
      setBrokers(data.brokers || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  const loadFederated = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/secret-brokers/federated`, { withCredentials: true });
      setFederated(data);
      // Refresh broker list to pick up new health/counts.
      const bList = await axios.get(`${API}/secret-brokers`, { withCredentials: true });
      setBrokers(bList.data.brokers || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const filteredSecrets = useMemo(() => {
    if (!federated?.secrets) return [];
    const q = search.trim().toLowerCase();
    if (!q) return federated.secrets;
    return federated.secrets.filter((s) =>
      `${s.name} ${s.path} ${s.broker_name} ${Object.entries(s.tags || {}).flat().join(' ')}`
        .toLowerCase().includes(q),
    );
  }, [federated, search]);

  return (
    <div className="min-h-screen text-slate-100 pb-24" data-testid="secret-brokers-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-amber-300" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="secret-brokers-heading">
                Secret Brokers
              </h1>
            </div>
            <p className="text-slate-400 text-sm max-w-2xl">
              Read-only federation with your existing secret stores.&nbsp;
              <b className="text-slate-200">CloudGenie never moves your secret values</b> —
              we only surface name, path, last-rotated, and access metadata so you
              can see rotation risk across every vault in one view.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadFederated}
              disabled={refreshing || brokers.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-700 hover:border-cyan-500/50 hover:bg-slate-900/60 text-slate-200 text-sm disabled:opacity-40"
              data-testid="secret-brokers-refresh-btn"
            >
              {refreshing
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Refreshing…</>
                : <><RefreshCw className="w-4 h-4" /> Refresh federated</>}
            </button>
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm font-medium"
              data-testid="secret-brokers-add-btn"
            >
              <Plus className="w-4 h-4" /> Add broker
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="secret-brokers-kpis">
          <Kpi title="Brokers"          value={brokers.length}                       Icon={KeyRound}       color="text-amber-300" />
          <Kpi title="Federated secrets" value={federated?.totals?.secrets ?? '—'}   Icon={Database}       color="text-cyan-300" />
          <Kpi title="Over threshold"    value={federated?.totals?.over_threshold ?? '—'} Icon={AlertTriangle} color="text-rose-300" />
          <Kpi title="Healthy brokers"   value={brokers.filter((b) => b.last_health === 'ok').length}
                Icon={CheckCircle2}      color="text-emerald-300" />
        </div>

        {/* Broker list */}
        <div className="mt-8">
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Brokers</div>
          {loading ? (
            <div className="text-slate-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading brokers…
            </div>
          ) : error ? (
            <div className="p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-rose-300 text-sm">{error}</div>
          ) : brokers.length === 0 ? (
            <div className="p-8 rounded-xl border border-dashed border-slate-800 bg-slate-900/30 text-center"
                  data-testid="secret-brokers-empty">
              <KeyRound className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <div className="font-semibold text-slate-200 mb-1">No brokers configured</div>
              <div className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                Add HashiCorp Vault, AWS Secrets Manager, Azure Key Vault, or GCP Secret Manager
                to start federating secret metadata.
              </div>
              <button onClick={() => setShowAdd(true)}
                       className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm">
                Add first broker
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="secret-brokers-grid">
              {brokers.map((b) => (
                <BrokerCard key={b.id} broker={b} onView={() => setActiveBroker(b)} onDeleted={load} />
              ))}
            </div>
          )}
        </div>

        {/* Federated view */}
        {federated && (
          <div className="mt-10" data-testid="secret-brokers-federated">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">Federated secrets</div>
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, path, broker, tag…"
                  className="w-full pl-9 pr-3 py-1.5 rounded-lg bg-slate-950 border border-slate-800 text-sm focus:outline-none focus:border-amber-500/60"
                  data-testid="federated-search"
                />
              </div>
              <div className="text-xs text-slate-500 ml-auto">
                {filteredSecrets.length} of {federated.totals.secrets} shown
              </div>
            </div>
            <FederatedTable rows={filteredSecrets} />
          </div>
        )}
      </div>

      {showAdd && (
        <AddBrokerModal onClose={() => setShowAdd(false)}
                          onAdded={() => { setShowAdd(false); load(); }} />
      )}
      {activeBroker && (
        <BrokerDrawer broker={activeBroker} onClose={() => setActiveBroker(null)}
                        onDeleted={() => { setActiveBroker(null); load(); }} />
      )}
    </div>
  );
}

function Kpi({ title, value, Icon, color }) {
  return (
    <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40"
          data-testid={`sb-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${color}`} />
        <div className="text-xs uppercase tracking-wide text-slate-500">{title}</div>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function BrokerCard({ broker, onView, onDeleted }) {
  const type = BROKER_TYPES[broker.type] || { label: broker.type, Icon: KeyRound, color: 'text-slate-300' };
  const TIcon = type.Icon;
  const h = HEALTH_STYLE[broker.last_health] || HEALTH_STYLE.unknown;
  const HIcon = h.Icon;
  const [busy, setBusy] = useState(false);

  const test = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      await axios.post(`${API}/secret-brokers/${broker.id}/test`, {}, { withCredentials: true });
      onDeleted();   // reuse to reload list
    } finally { setBusy(false); }
  };

  return (
    <button
      onClick={onView}
      className="text-left p-4 rounded-xl border border-slate-800 bg-slate-900/40 hover:border-amber-500/40 transition-all"
      data-testid={`broker-card-${broker.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex items-center gap-2">
          <TIcon className={`w-4 h-4 ${type.color}`} />
          <div className="font-semibold text-slate-100 truncate">{broker.name}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono border ${h.bg} ${h.color} flex items-center gap-1`}>
          <HIcon className="w-3 h-3" /> {h.label}
        </span>
      </div>
      <div className="text-xs text-slate-500">{type.label}</div>
      {broker.description && <div className="text-xs text-slate-400 mt-2 line-clamp-2">{broker.description}</div>}
      <div className="mt-3 flex gap-2 text-[11px]">
        <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300">
          {broker.secret_count} secrets
        </span>
        <span className="px-2 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-300">
          rotate ≥ {broker.rotation_threshold_days}d
        </span>
      </div>
      {broker.last_error && (
        <div className="mt-2 text-[11px] text-rose-300 line-clamp-2">{broker.last_error}</div>
      )}
      <div className="mt-3 pt-3 border-t border-slate-800 flex justify-between items-center">
        <div className="text-[10px] text-slate-500">
          {broker.last_synced_at ? `Synced ${new Date(broker.last_synced_at).toLocaleString()}` : 'Never synced'}
        </div>
        <button onClick={test} disabled={busy}
                 className="text-[11px] text-amber-300 hover:text-amber-200 disabled:opacity-50"
                 data-testid={`broker-test-${broker.id}`}>
          {busy ? 'Testing…' : 'Test connection'}
        </button>
      </div>
    </button>
  );
}

function FederatedTable({ rows }) {
  if (rows.length === 0) {
    return (
      <div className="p-6 rounded-xl border border-slate-800 bg-slate-900/30 text-center text-sm text-slate-500"
            data-testid="federated-empty">
        No secrets found. Click <b>Refresh federated</b> to pull the latest listing.
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 overflow-hidden"
          data-testid="federated-table-container">
      <table className="w-full text-sm">
        <thead className="bg-slate-950/60">
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-4 py-2">Name</th>
            <th className="px-4 py-2">Broker</th>
            <th className="px-4 py-2">Age</th>
            <th className="px-4 py-2">Tags</th>
            <th className="px-4 py-2">Path</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.broker_id}-${r.path}-${i}`} className="border-t border-slate-800/60 hover:bg-slate-900/60"
                 data-testid={`federated-row-${i}`}>
              <td className="px-4 py-2 font-mono text-slate-200 truncate max-w-[220px]">{r.name}</td>
              <td className="px-4 py-2 text-xs text-slate-400">{r.broker_name}</td>
              <td className={`px-4 py-2 text-xs ${r.over_rotation_threshold ? 'text-rose-300 font-semibold' : 'text-slate-400'}`}>
                {r.age_days == null ? '—' : `${r.age_days}d`}{r.over_rotation_threshold && ' ⚠'}
              </td>
              <td className="px-4 py-2 text-xs text-slate-400 truncate max-w-[180px]">
                {Object.entries(r.tags || {}).map(([k, v]) => (
                  <span key={k} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">
                    {k}={v}
                  </span>
                ))}
              </td>
              <td className="px-4 py-2 text-xs text-slate-500 font-mono truncate max-w-[280px]">{r.path}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AddBrokerModal({ onClose, onAdded }) {
  const [type, setType] = useState('hashicorp_vault');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [threshold, setThreshold] = useState(90);
  const [config, setConfig] = useState({});
  const [alertType, setAlertType] = useState('');   // '' | slack | teams | webhook
  const [alertUrl, setAlertUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const spec = BROKER_TYPES[type];
  const alertOk = !alertType || /^https?:\/\//i.test(alertUrl.trim());
  const canSubmit = name.trim() && spec.fields.every((f) => !f.required || config[f.key]) && alertOk;

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      // Parse GCP service_account_json if it looks like a JSON string.
      const payload = { ...config };
      if (type === 'gcp_secret_manager' && typeof payload.service_account_json === 'string') {
        try { payload.service_account_json = JSON.parse(payload.service_account_json); }
        catch { /* leave as string — backend will store it either way */ }
      }
      const body = { name, type, description, config: payload,
                     rotation_threshold_days: Number(threshold) };
      if (alertType && alertUrl.trim()) {
        body.alert_webhook_type = alertType;
        body.alert_webhook_url  = alertUrl.trim();
      }
      await axios.post(`${API}/secret-brokers`, body, { withCredentials: true });
      onAdded();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-broker-modal">
      <div className="fixed inset-0 bg-slate-950/70" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-slate-900 border border-slate-800 rounded-xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold">Add Secret Broker</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Broker type</div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(BROKER_TYPES).map(([k, v]) => {
                const TIcon = v.Icon;
                return (
                  <button key={k} onClick={() => { setType(k); setConfig({}); }}
                           data-testid={`broker-type-${k}`}
                           className={`p-3 rounded-lg border text-left flex items-start gap-2 ${
                             type === k ? 'border-amber-500/60 bg-amber-500/10' : 'border-slate-800 hover:border-slate-700'
                           }`}>
                    <TIcon className={`w-4 h-4 mt-0.5 shrink-0 ${v.color}`} />
                    <span className="flex-1 min-w-0">
                      <span className="text-sm block truncate">{v.label}</span>
                      {v.hint && <span className="text-[10px] text-slate-500 block truncate">{v.hint}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <label className="block">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Name</div>
            <input value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="prod-vault"
                    className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500/60"
                    data-testid="ab-name" />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Description</div>
            <input value={description} onChange={(e) => setDescription(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500/60" />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">Rotation threshold (days)</div>
            <input type="number" min={1} value={threshold} onChange={(e) => setThreshold(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500/60" />
          </label>
          <div className="pt-2 border-t border-slate-800">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Rotation alerts (optional)</div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              {[['', 'None'], ['slack', 'Slack'], ['teams', 'Teams'], ['webhook', 'Generic webhook']].map(([id, label]) => (
                <button key={id || 'none'} onClick={() => setAlertType(id)}
                  data-testid={`alert-channel-${id || 'none'}`}
                  className={`p-2 rounded-lg border text-xs ${alertType === id ? 'border-amber-500/60 bg-amber-500/10 text-amber-200' : 'border-slate-800 text-slate-400 hover:border-slate-700'}`}>
                  {label}
                </button>
              ))}
            </div>
            {alertType && (
              <label className="block">
                <div className="text-xs text-slate-400 mb-1">
                  {alertType === 'slack' ? 'Slack incoming-webhook URL'
                    : alertType === 'teams' ? 'Teams incoming-webhook URL'
                    : 'Webhook POST URL (JSON body)'}
                  <span className="text-rose-300"> *</span>
                </div>
                <input value={alertUrl} onChange={(e) => setAlertUrl(e.target.value)}
                  placeholder={alertType === 'slack' ? 'https://hooks.slack.com/services/...' : 'https://...'}
                  className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500/60"
                  data-testid="alert-webhook-url" />
                <div className="text-[11px] text-slate-500 mt-1">
                  Fires once per secret when it crosses the rotation threshold, then again every 30 days it stays overdue.
                </div>
              </label>
            )}
          </div>
          <div className="pt-2 border-t border-slate-800">
            <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">{spec.label} configuration</div>
            {spec.fields.map((f) => (
              <label key={f.key} className="block mb-2">
                <div className="text-xs text-slate-400 mb-1">
                  {f.label}{f.required && <span className="text-rose-300"> *</span>}
                </div>
                {f.textarea ? (
                  <textarea rows={5}
                             value={config[f.key] || ''} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                             placeholder={f.placeholder}
                             className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-amber-500/60"
                             data-testid={`ab-field-${f.key}`} />
                ) : (
                  <input type={f.secret ? 'password' : 'text'}
                          value={config[f.key] || ''} onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
                          placeholder={f.placeholder}
                          className="w-full bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-amber-500/60"
                          data-testid={`ab-field-${f.key}`} />
                )}
              </label>
            ))}
          </div>
          {error && <div className="text-xs text-rose-300">{error}</div>}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400">Cancel</button>
          <button onClick={submit} disabled={!canSubmit || submitting}
                   className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black text-sm disabled:opacity-40"
                   data-testid="ab-submit">
            {submitting ? 'Adding…' : 'Add broker'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BrokerDrawer({ broker, onClose, onDeleted }) {
  const [secrets, setSecrets] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const type = BROKER_TYPES[broker.type] || { label: broker.type };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [s, a] = await Promise.all([
          axios.get(`${API}/secret-brokers/${broker.id}/secrets`, { withCredentials: true }),
          axios.get(`${API}/secret-brokers/${broker.id}/alerts?limit=50`, { withCredentials: true }),
        ]);
        if (alive) { setSecrets(s.data); setAlerts(a.data); }
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [broker.id]);

  const sendTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const { data } = await axios.post(`${API}/secret-brokers/${broker.id}/alerts/test`,
        {}, { withCredentials: true });
      setTestResult(data);
    } catch (e) {
      setTestResult({ status: 'failed', error: e?.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  const del = async () => {
    if (!window.confirm('Remove this broker connection? CloudGenie does NOT delete anything in the source vault.')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/secret-brokers/${broker.id}`, { withCredentials: true });
      onDeleted();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setDeleting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="broker-drawer">
      <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-slate-900 border-l border-slate-800 shadow-2xl overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-500 hover:text-slate-200"
                 data-testid="broker-drawer-close">
          <X className="w-5 h-5" />
        </button>
        <div className="p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">{type.label}</div>
            <div className="text-2xl font-bold" data-testid="broker-drawer-name">{broker.name}</div>
            {broker.description && <div className="text-sm text-slate-400 mt-1">{broker.description}</div>}
          </div>
          {err && <div className="p-3 rounded-lg border border-rose-500/40 bg-rose-500/5 text-rose-300 text-sm">{err}</div>}
          {loading ? (
            <div className="text-slate-500 text-sm flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching secret metadata…
            </div>
          ) : secrets ? (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="text-[10px] uppercase text-slate-500">Secrets</div>
                  <div className="text-xl font-semibold text-cyan-300">{secrets.count}</div>
                </div>
                <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="text-[10px] uppercase text-slate-500">Over threshold</div>
                  <div className="text-xl font-semibold text-rose-300">{secrets.over_threshold}</div>
                </div>
                <div className="p-3 rounded-lg bg-slate-950 border border-slate-800">
                  <div className="text-[10px] uppercase text-slate-500">Threshold</div>
                  <div className="text-xl font-semibold text-slate-200">{broker.rotation_threshold_days}d</div>
                </div>
              </div>
              <FederatedTable rows={secrets.secrets} />

              <div className="pt-3 border-t border-slate-800">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-200">Rotation alerts</div>
                    <div className="text-[11px] text-slate-500">
                      {broker.alert_channel_configured
                        ? `Wired to ${broker.alert_webhook_type}. Fires once when a secret first crosses the threshold, then every 30d it stays overdue.`
                        : 'No alert channel configured. Add one when creating a broker to get Slack/Teams/webhook nudges.'}
                    </div>
                  </div>
                  {broker.alert_channel_configured && (
                    <button onClick={sendTest} disabled={testing}
                      data-testid="broker-alerts-test-btn"
                      className="text-xs px-3 py-1.5 rounded border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 disabled:opacity-50">
                      {testing ? 'Sending…' : 'Send test'}
                    </button>
                  )}
                </div>
                {testResult && (
                  <div className={`text-xs mb-2 px-2 py-1.5 rounded ${testResult.status === 'sent' ? 'bg-emerald-500/10 text-emerald-300' : 'bg-rose-500/10 text-rose-300'}`}
                    data-testid="broker-alerts-test-result">
                    {testResult.status === 'sent'
                      ? `Test alert delivered via ${testResult.channel}.`
                      : `Delivery failed: ${testResult.error || 'unknown'}`}
                  </div>
                )}
                {alerts && alerts.alerts && alerts.alerts.length > 0 ? (
                  <div className="max-h-56 overflow-y-auto border border-slate-800 rounded">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-slate-500 bg-slate-950 sticky top-0">
                        <tr>
                          <th className="px-2 py-1 text-left">Fired</th>
                          <th className="px-2 py-1 text-left">Secret</th>
                          <th className="px-2 py-1 text-left">Age@alert</th>
                          <th className="px-2 py-1 text-left">Delivery</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alerts.alerts.map((a, i) => (
                          <tr key={i} className="border-t border-slate-800/60" data-testid={`broker-alert-row-${i}`}>
                            <td className="px-2 py-1 text-slate-400 whitespace-nowrap">{(a.fired_at || '').slice(0, 19).replace('T', ' ')}</td>
                            <td className="px-2 py-1 font-mono text-slate-300 truncate max-w-[240px]">{a.secret_path}</td>
                            <td className="px-2 py-1 text-rose-300">{a.age_days_at_alert}d</td>
                            <td className={`px-2 py-1 ${a.delivery_status === 'sent' ? 'text-emerald-300' : 'text-rose-300'}`}>
                              {a.delivery_status}{a.delivery_error ? ` — ${a.delivery_error.slice(0, 60)}` : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-500 italic">No alerts fired yet.</div>
                )}
              </div>
            </>
          ) : null}
          <div className="pt-3 border-t border-slate-800 flex justify-end">
            <button onClick={del} disabled={deleting}
                     className="text-sm text-rose-400 hover:text-rose-300 disabled:opacity-50"
                     data-testid="broker-drawer-delete">
              {deleting ? 'Removing…' : 'Remove broker connection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
